import { useMutation, useQueryClient } from "@tanstack/react-query";
import { eventsApi } from "@/lib/humhub-api/events";
import type { RecurrenceScope } from "@/lib/humhub-api/types";
import { queryInvalidationHelpers } from "@/lib/queryInvalidationHelpers";

interface UseDeleteEventRemindersOptions {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

/**
 * Hook for deleting all reminders for a user for a specific event
 */
export const useDeleteEventReminders = (
  options?: UseDeleteEventRemindersOptions,
) => {
  const queryClient = useQueryClient();

  const {
    mutateAsync: deleteReminders,
    isPending,
    error,
  } = useMutation({
    mutationFn: ({
      eventId,
      scope,
    }: {
      eventId: number;
      scope?: RecurrenceScope;
    }) => eventsApi.deleteAllEventReminders(eventId, scope),
    onSuccess: () => {
      // Invalidate relevant queries to refresh data
      queryInvalidationHelpers.invalidateEventQueries(queryClient);

      // Call the success callback if provided
      if (options?.onSuccess) {
        options.onSuccess();
      }
    },
    onError: (error: Error) => {
      console.error("Failed to delete event reminders:", error);

      // Call the error callback if provided
      if (options?.onError) {
        options.onError(error);
      }
    },
  });

  return {
    deleteReminders: (eventId: number, scope?: RecurrenceScope) =>
      deleteReminders({ eventId, scope }),
    isLoading: isPending,
    error,
  };
};
